import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';
import { PageHead, Section, Spinner, useAsync } from '../components/ui';
import { formatPhoneInput } from '../lib/phone';

/**
 * My Profile — single source of truth for the Person fields consumed by
 * the Event registration wizard (design/024): firstname, lastname, email,
 * phone. Username is also editable here.
 *
 * If the caller was sent here from a wizard (`location.state.returnTo`),
 * a "Continue registration →" button appears after Save so they can hop
 * back without hunting for the tab.
 */
export default function ProfilePage() {
  const { user, me, refreshMe, signOut } = useAuth();
  const navigate = useNavigate();
  const returnTo = useLocation().state?.returnTo || null;

  const [username, setUsername] = useState(me?.username && me.username !== me.email ? me.username : '');
  const [firstname, setFirstname] = useState(me?.firstname || '');
  const [lastname, setLastname]   = useState(me?.lastname || '');
  const [phone, setPhone]         = useState(formatPhoneInput(me?.phone || ''));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [saved, setSaved] = useState(false);

  // Fetch fresh details every time Profile opens (settings may have been
  // changed on the phone since sign-in, e.g. Email preferences).
  useEffect(() => {
    refreshMe().then((m) => {
      if (!m) return;
      setUsername(m.username && m.username !== m.email ? m.username : '');
      setFirstname(m.firstname || '');
      setLastname(m.lastname || '');
      setPhone(formatPhoneInput(m.phone || ''));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!username.trim()) return;
    setBusy(true); setMsg(null); setSaved(false);
    try {
      await api.updateMe({
        username: username.trim(),
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        phone: phone.trim(),
      });
      await refreshMe();
      setMsg('Saved.');
      setSaved(true);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm('Permanently delete your account? This cannot be undone.')) return;
    try { await api.deleteMe(); await signOut(); } catch (e) { alert(e.message); }
  };

  return (
    <div className="page">
      <PageHead title="Profile" />
      <div className="grid-2">
        <div>
          <Section title="Account">
            <div className="form-field"><label>Email</label><input className="input" value={me?.email || user?.email || ''} disabled /></div>
            <div className="form-field"><label>Username</label><input className="input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Pick a username" /></div>
            <div className="form-field"><label>First Name</label><input className="input" value={firstname} onChange={(e) => setFirstname(e.target.value)} autoComplete="given-name" /></div>
            <div className="form-field"><label>Last Name</label><input className="input" value={lastname} onChange={(e) => setLastname(e.target.value)} autoComplete="family-name" /></div>
            <div className="form-field"><label>Phone Number</label><input className="input" type="tel" value={phone} onChange={(e) => setPhone(formatPhoneInput(e.target.value, phone))} autoComplete="tel" placeholder="e.g. +1 555 123 4567" /></div>
            {msg && <div style={{ fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: 8 }}>{msg}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" disabled={busy || !username.trim()} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
              {returnTo && saved && (
                <button className="btn" onClick={() => navigate(returnTo)}>Continue registration →</button>
              )}
            </div>
          </Section>
        </div>
        <div>
          <EmailPreferences />
          <BlockedPeople />
          <Section title="Session">
            <button className="btn" style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }} onClick={signOut}>Sign out</button>
            <div className="danger-zone">
              <div className="label">Danger zone</div>
              <button className="btn btn-danger" style={{ width: '100%', justifyContent: 'center' }} onClick={del}>Delete account</button>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

// design/029 — people you've blocked in conversations; unblock here.
function BlockedPeople() {
  const { loading, data, reload } = useAsync(
    () => api.listBlocks().then((r) => (Array.isArray(r) ? r : [])).catch(() => []), []);
  const [busy, setBusy] = useState(null);
  const unblock = async (b) => {
    if (!window.confirm(`Unblock ${b.name}? You'll see their posts and replies again.`)) return;
    setBusy(b.personId);
    try { await api.unblockPerson(b.personId); await reload(); }
    catch (e) { alert(e?.data?.message || e?.message || 'Could not unblock'); }
    finally { setBusy(null); }
  };
  return (
    <Section title="Blocked people">
      {loading ? <Spinner /> : data.length === 0 ? (
        <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
          You haven't blocked anyone. You can block someone from the ⚑ Report or ⋯ menu on their post.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.map((b) => (
            <div key={b.personId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{b.name}</b>
                {b.blockedAt && (
                  <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}> · since {new Date(b.blockedAt).toLocaleDateString()}</span>
                )}
              </div>
              <button className="btn btn-sm" disabled={busy === b.personId} onClick={() => unblock(b)}>
                {busy === b.personId ? 'Unblocking…' : 'Unblock'}
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/** design/035 — "New courses and practices" announcement emails on/off. Saves on change. */
function EmailPreferences() {
  const { me, refreshMe } = useAuth();
  const [on, setOn] = useState(me?.announceEmails !== false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setOn(me?.announceEmails !== false); }, [me?.announceEmails]);
  const change = async (v) => {
    setOn(v); setBusy(true);
    try { await api.updateMe({ announceEmails: v }); await refreshMe(); }
    catch (e) { setOn(!v); alert(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Section title="Email preferences">
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={on} disabled={busy} onChange={(e) => change(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          <b>New courses, practices, events and groups</b>
          <div style={{ color: 'var(--muted)', fontSize: '0.8125rem' }}>Get an email when something new is published.</div>
        </span>
      </label>
    </Section>
  );
}
